import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {EmployeeAppraisalDataService} from './employee-appraisal-data.service';
import {ConfirmationService, MenuItem, SelectItem} from 'primeng/api';
import {plainToClass} from 'class-transformer';
import {Subscription} from "rxjs";

import {
  AppraisalAccessedBy,
  AppraisalStatus,
  BreadcrumbMain,
  BreadcrumbMSS,
  ClassType,
  CommonTransformationService,
  DirectoryService,
  EmployeeAppraisalLog,
  EmployeeAppraisalSummary,
  FlsaType,
  Format,
  GrowlType,
  HrService,
  OrgAppraisalPeriod,
  PositionDescription,
  PrimeNgConfig,
  SignedStatus,
  TableColumn,
  TableFormat,
  Validation,
} from '@app/shared';
import {GrowlService, UserService} from '@app/core';

@Component({
  selector: 'app-employee-appraisal',
  templateUrl: './employee-appraisal.component.html',
  styleUrls: ['./employee-appraisal.component.css'],
  providers: [EmployeeAppraisalDataService, ConfirmationService],
})

/**
 * @author Archana Anand (date: Aug 2018)
 * @desc Manager Appraisal component that has details of all employee appraisals of the manager
 * */

export class EmployeeAppraisalComponent implements OnInit, OnChanges {

  @Input() viewedBy: AppraisalAccessedBy;
  @Input() year: string;
  @Input() appraisalPeriod: OrgAppraisalPeriod;
  @Output() handler = new EventEmitter<OrgAppraisalPeriod>();

  Format: typeof Format = Format;
  AppraisalStatus: typeof AppraisalStatus = AppraisalStatus;
  AppraisalViewedBy: typeof AppraisalAccessedBy = AppraisalAccessedBy;
  ClassType: typeof ClassType = ClassType;
  Validation: typeof Validation = Validation;
  FlsaType: typeof FlsaType = FlsaType;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;


  private AllYear: string = "All";
  private SalaryRecommendation: string [] = ["LQ: Lower Quartile", "Q1: 1st Quartile", "Q2: 2nd Quartile", "Q3: 3rd Quartile", "Q4: 4th Quartile", "UQ: Upper" +
  " Quartile"];

  breadcrumb: MenuItem[] = [];
  tableColumns: TableColumn[];                                // column headers and fields to display on the table
  logsTableColumns: TableColumn[];                            // column headers and fields to display on the Appraisal log table
  userId: number;                                   // id of the manager who has logged in
  positions: PositionDescription[];
  positionDropdown: SelectItem[] = [];              // list of position descriptions of the employees for the dropdown
  appraisalSummaries: EmployeeAppraisalSummary[];   // employee appraisal summary displayed in the table
  selectedAppraisalSummary: EmployeeAppraisalSummary;
  appraisalLog: EmployeeAppraisalLog;               // appraisal log to complete and send
  appraisalLogs: EmployeeAppraisalLog[] = [];       // list of appraisal logs for each appraisal
  appraisalLogDialog: boolean = false;              // dialog to fill out appraisal log
  isCloseAppraisalPeriod: boolean = false;          // boolean to check whether the appraisal period can be closed or not

  /* list to populate the dropdowns which are the search criteria for the filtering appraisals */
  appraisalPeriodsDropdown: SelectItem[];        // list of appraisal periods
  managersDropdown: SelectItem[];                // list of direct reports of the manager during Manager access or all the managers in that organization during HR access
  obsDropdown: SelectItem[];                     // list of obs/departments
  signedByUserDropdown: SelectItem[];            // list containing SIGNED or UNSIGNED status of appraisal

  /* selected objects from the dropdown */
  selectedPeriod: OrgAppraisalPeriod;
  selectedManagerId: number;
  selectedObsId: number;
  selectedSignedByUser: SignedStatus;

  // summary of appraisals
  totalBonus: number;
  avgPercentIncrease: number;
  totalSignedByEmployee: number;
  totalSignedByManager: number;

  userInfoSub: Subscription;                      // subscription to get user info
                                                  // in case this component is loaded before user info is retrieved from backend

  constructor(private dataService: EmployeeAppraisalDataService, private transform: CommonTransformationService, private hrService: HrService,
              private user: UserService, private growl: GrowlService, private confirmationService: ConfirmationService,
              private directoryService: DirectoryService) {

    // set the userId to the user service; this happens when routed directly to this component
    this.userInfoSub = UserService.userInfoSub.subscribe(userInfoAvailable => {
      if (userInfoAvailable) {
        this.userId = this.user.info.userId;
        if (this.viewedBy == AppraisalAccessedBy.Manager && !this.selectedManagerId) {
          this.selectedManagerId = this.userId;
          // retrieve appraisals for manager's subordinates because since user id is not available
          if(this.selectedPeriod){
            this.searchAppraisals(this.selectedPeriod.id, this.selectedManagerId, null, null);
          }
        }
      }
    });
  }

  ngOnInit() {

    if(this.user.info){
      this.userId = this.user.info.userId;
    }

    this.breadcrumb = [];
    this.breadcrumb.push(BreadcrumbMain);
    this.breadcrumb.push(BreadcrumbMSS);
    this.breadcrumb.push({label: 'Employee Appraisal', routerLink: '/hcm/common/employee-appraisal'});

    this.buildDropdown();

    console.log("ngOnInit: viewedBy = " + this.viewedBy);
    if (!this.viewedBy) {
      this.viewedBy = AppraisalAccessedBy.Manager;
      console.log("ngOnInit: viewedBy changed to = " + this.viewedBy);
      this.getAppraisalPeriods();
    }

    this.tableColumns = [
      {field: 'employeeName', header: 'Emp Name'},
      {field: 'currentPositionTitle', header: 'Current Position'},
      {field: 'newPositionTitle', header: 'New Position'},
      {field: 'overallRating', subfield: 'name', header: 'Employee Rating'},
      {field: 'currentAnnualSalary', header: 'Current Annual Salary'},
      {field: 'newAnnualSalary', header: 'New Annual Salary'},
      {field: 'currentHourlySalary', header: 'Current Hourly Salary'},
      {field: 'newHourlySalary', header: 'New Hourly Salary'},
      {field: 'salaryRank', header: 'Quartile & Rec Increase'},
      {field: 'percentIncrease', header: '% Increase'},
      {field: 'amountIncrease', header: 'Increase Amount'},
      {field: 'bonus', header: 'Bonus'},
      {field: 'signedByManagerOn', header: 'Manager Completed'},
      {field: 'action', header: 'Actions'}
    ];

    this.logsTableColumns = [
      {field: 'updatedOn', header: 'Date', format: TableFormat.Date},
      {field: 'updatedByName', header: 'Updated By'},
      // {field: 'updatedByDepartment', header: 'Department'},
      {field: 'positionTitle', header: 'Position'},
      {field: 'percentIncrease', header: '% Increase'},
      {field: 'bonus', header: 'Bonus', format: TableFormat.Currency},
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log("ngOnChanges: viewedBy = " + this.viewedBy);

    this.getAppraisalPeriods();
  }

  buildDropdown() {
    this.signedByUserDropdown = this.transform.convertEnumValueToDropdown(SignedStatus);
    this.obsDropdown = [];
    this.directoryService.getObsInfoAsDropdown().subscribe(res => {
      this.obsDropdown = res;
    });
    this.positionDropdown = [];
    this.hrService.getPositions().subscribe(res => {
      this.positions = plainToClass(PositionDescription, res.list);
      this.positionDropdown = this.transform.convertToDropDown('positionTitle', res.list, 'id');
    });
  }


  /**
   * @desc: gets the list of appraisal period and populates the dropdown where the OPEN appraisal period is selected by default
   */
  getAppraisalPeriods() {
    this.dataService.searchAppraisalPeriods().subscribe(res => {
      if (this.year && this.year != this.AllYear) {
        res.list = res.list.filter(appraisalPeriod => (appraisalPeriod.dateThru.substring(0, 4) == this.year));
      }
      this.appraisalPeriodsDropdown = this.transform.convertToDropDown('name', res.list);

      let appraisalPeriod;
      if (this.appraisalPeriod) {
        appraisalPeriod = this.appraisalPeriodsDropdown.find(period => (period.value.id == this.appraisalPeriod.id));
      } else {
        appraisalPeriod = this.appraisalPeriodsDropdown.find(period => (period.value.appraisalStatus == AppraisalStatus.OPEN));
      }
      if (appraisalPeriod) {
        this.selectedPeriod = appraisalPeriod.value;
        if (this.viewedBy == AppraisalAccessedBy.Hr) {
          this.getManagersList(this.selectedPeriod.id);
        }
      }
      if (this.viewedBy == AppraisalAccessedBy.Manager) {
        this.getSubordinateObsForSelf();
        this.selectedManagerId = this.userId;

        if (this.selectedManagerId) {
          // retrieve the appraisal for manager; not for HR
          this.searchAppraisals(this.selectedPeriod.id, this.selectedManagerId, null, null);
        }
      }
    });
  }

  /**
   * @desc inform the parent component that appraisal period has changed
   */
  onChangeAppraisalPeriod(appraisalPeriod: OrgAppraisalPeriod) {
    this.appraisalSummaries = [];
    this.totalSignedByEmployee = null;
    this.totalSignedByManager = null;
    this.avgPercentIncrease = null;
    this.totalBonus = null;
    this.handler.emit(appraisalPeriod);
  }

  /**
   * @desc: gets the list of direct reports of the logged in manager and populates the dropdown where the logged in manager  is selected by default
   */
  getSubordinateObsForSelf() {
    this.managersDropdown = [];
    this.dataService.getSubordinateObsForSelf().subscribe(res => {
      // ToDo may have to include the managers...
      this.managersDropdown = this.transform.convertToLongLabelDropDown(['name', 'essOwnerName'], res.list, 'essOwner', false, " - ");
    });
  }

  /**
   * @desc: gets the list of managers to be viewed by Hr Admin
   */
  getManagersList(appraisalPeriodId: number) {
    this.dataService.getManagersList(appraisalPeriodId).subscribe(res => {
      this.managersDropdown = this.transform.convertToLongLabelDropDown(['name', 'essOwnerName'], res, 'id', false, " - ");
    });
  }


  /**
   * @desc: gets the list of appraisals based on manager, appraisal period, department and signed or not. If all the appraisals are signed by manager, then the org appraisal period can be closed.
   */
  searchAppraisals(appraisalPeriodId: number, managerId: number, obsId: number, signedByUser) {
    this.dataService.searchAppraisalSummary(managerId, appraisalPeriodId, obsId, signedByUser).subscribe(res => {
      this.appraisalSummaries = plainToClass(EmployeeAppraisalSummary, res.list);
      /* isCloseAppraisalPeriod - defines whether the appraisal can be closed or not based on certain conditions for an OPEN appraisal period otherwise it is always false */
      if (this.selectedPeriod.appraisalStatus == AppraisalStatus.OPEN) {
        this.isCloseAppraisalPeriod = this.canAppraisalPeriodBeClosed(this.appraisalSummaries);
      } else {
        this.isCloseAppraisalPeriod = false;
      }
      this.calculateTotals(this.appraisalSummaries);
    });
  }

  /**
   * @desc: This method will check whether all org appraisal period can be closed or not. If all the appraisals are signed by manager, then the org appraisal period can be closed. This can be unit test.
   */
  canAppraisalPeriodBeClosed(appraisals: EmployeeAppraisalSummary[]): boolean {
    let isCloseAppraisalPeriod = false;
    if (!appraisals.find(appr => (!appr.signedByManagerOn))) {
      isCloseAppraisalPeriod = true;
    }
    return isCloseAppraisalPeriod;
  }

  /**
   * @desc: gets a list of appraisal logs for the selected appraisal.
   */
  getAppraisalLogsOfAppraisal(appraisalId: number) {
    this.appraisalLogs = [];
    this.dataService.searchAppraisalLogs(appraisalId).subscribe(logs => {
      this.appraisalLogs = plainToClass(EmployeeAppraisalLog, logs.list);
    });
  }

  /**
   * @desc: editing the appraisal that creates a new object appraisalToEdit where its information such percentIncrease, bonus and position is modified
   */
  onClickNewAppraisalLog(appraisalSummary: EmployeeAppraisalSummary) {
    if (appraisalSummary) {
      this.selectedAppraisalSummary = appraisalSummary;
      this.appraisalLog = new EmployeeAppraisalLog();
      this.appraisalLog.appraisalId = appraisalSummary.id;
      this.appraisalLog.positionId = appraisalSummary.newPositionId;
      this.appraisalLog.positionTitle = appraisalSummary.newPositionTitle;
      this.appraisalLog.percentIncrease = appraisalSummary.percentIncrease;
      this.appraisalLog.bonus = appraisalSummary.bonus;
      this.appraisalLogDialog = true;
    }
  }

  onChangePosition(appraisalLog: EmployeeAppraisalLog) {
    let position = this.positions.find(pos => pos.id == appraisalLog.positionId);
    appraisalLog.positionTitle = position.positionTitle;
  }


  /**
   * @desc: displays the list of appraisal logs in an overlay panel
   */
  onClickShowAppraisalLog(appraisalSummary: EmployeeAppraisalSummary, event, overlayPanel) {
    this.selectedAppraisalSummary = appraisalSummary;
    this.getAppraisalLogsOfAppraisal(appraisalSummary.id);
    overlayPanel.toggle(event);
  }


  saveAppraisalLog(appraisalLog: EmployeeAppraisalLog) {
    this.dataService.saveAppraisalLogs(appraisalLog).subscribe(res => {
      let savedAppraisalLog = plainToClass(EmployeeAppraisalLog, res as EmployeeAppraisalLog);
      this.searchAppraisals(this.selectedPeriod.id, this.selectedManagerId, this.selectedObsId, this.selectedSignedByUser);
      this.appraisalLogDialog = false;
    });
  }

  /**
   * @desc: sends overdue notice to the direct report managers and employees
   */
  onClickSendOverdueNotice() {
    this.dataService.sendOverdueNotice(this.selectedPeriod.id).subscribe(res => {
      this.growl.add(GrowlType.Success, `Overdue notice sent.`);
    });
  }

  showSalaryRankTooltip(): string {
    let tooltipString: string = ``;
    for (let code of this.SalaryRecommendation) {
      tooltipString += `<span>${code}</span><br>`;
    }
    return tooltipString;
  }

  private calculateTotals(appraisalSummaries: EmployeeAppraisalSummary[]) {
    this.totalBonus = 0;
    this.totalSignedByEmployee = 0;
    this.totalSignedByManager = 0;
    this.avgPercentIncrease = 0;
    this.appraisalSummaries.forEach(appSumm => {
      if (appSumm.signedByUser) {
        this.totalSignedByEmployee++;
      }
      if (appSumm.signedByManager) {
        this.totalSignedByManager++;
      }
      if (appSumm.bonus) {
        this.totalBonus += appSumm.bonus;
      }
      if (appSumm.percentIncrease) {
        this.avgPercentIncrease += appSumm.percentIncrease;
      }
    });
    if (this.appraisalSummaries.length > 0) {
      this.avgPercentIncrease = this.avgPercentIncrease / this.appraisalSummaries.length;
    }
  }
}
